import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { VacationCard, SicknessCard } from '../components/RequestCards';
import { AttendanceDailyView, AttendanceMonthlyReport } from '../components/AttendanceViews';
import WorkplaceManager from '../components/WorkplaceManager';
import EmployeeManager from '../components/EmployeeManager';
import { PayslipManagerHR } from '../components/Payslips';
import { AnnouncementManager } from '../components/Announcements';
import NotificationSettings from '../components/NotificationSettings';
import LanguageSelector from '../components/LanguageSelector';
import { formatDate } from '../lib/formatters';
import {
  apiListVacations, apiReviewVacation, apiListSickness, apiListAttendance, apiListWorkplaces
} from '../api';
import {
  LogOut, Briefcase, ClipboardList, Stethoscope, CheckCircle2, XCircle,
  Search, Hourglass, Users, CalendarDays, AlertCircle, Fingerprint, Building2, BarChart3,
  Wallet, Megaphone, UserCog, Bell
} from 'lucide-react';
import HCLogo from '../components/HCLogo';
import AppSwitcher from '../components/AppSwitcher';
import { toast } from 'sonner';
import { logError } from '../lib/logger';

const HRDashboardPage = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [vacationRequests, setVacationRequests] = useState([]);
  const [sicknessProtocols, setSicknessProtocols] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [workplacesList, setWorkplacesList] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('attendance');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const reload = async () => {
    try {
      const [vac, sick, att, wps] = await Promise.all([
        apiListVacations(), apiListSickness(), apiListAttendance(), apiListWorkplaces()
      ]);
      setVacationRequests(vac || []);
      setSicknessProtocols(sick || []);
      setAttendance(att || []);
      setWorkplacesList(wps || []);
    } catch (e) {
      logError('HR dashboard reload failed:', e);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todayAtt = attendance.filter(a => a.timestamp.startsWith(today));
    const presentEmployees = new Set();
    const empMap = {};
    todayAtt.forEach(a => {
      if (!empMap[a.employeeEmail]) empMap[a.employeeEmail] = [];
      empMap[a.employeeEmail].push(a);
    });
    Object.entries(empMap).forEach(([email, events]) => {
      events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const last = events[events.length - 1];
      if (last && last.type !== 'uscita') presentEmployees.add(email);
    });
    return {
      pending: vacationRequests.filter(r => r.status === 'pending').length,
      approved: vacationRequests.filter(r => r.status === 'approved').length,
      rejected: vacationRequests.filter(r => r.status === 'rejected').length,
      sickness: sicknessProtocols.length,
      presentToday: presentEmployees.size,
      workplaces: workplacesList.filter(w => w.active).length,
      employees: new Set([...vacationRequests.map(r => r.employeeEmail), ...sicknessProtocols.map(r => r.employeeEmail), ...attendance.map(a => a.employeeEmail)]).size
    };
  }, [vacationRequests, sicknessProtocols, attendance, workplacesList]);

  const filteredVacations = useMemo(() => {
    let list = [...vacationRequests];
    if (filter !== 'all') list = list.filter(r => r.status === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.employeeName?.toLowerCase().includes(q) ||
        r.employeeEmail?.toLowerCase().includes(q) ||
        r.type?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (b.status === 'pending' && a.status !== 'pending') return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [vacationRequests, filter, search]);

  const filteredSickness = useMemo(() => {
    let list = [...sicknessProtocols];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.employeeName?.toLowerCase().includes(q) ||
        r.employeeEmail?.toLowerCase().includes(q) ||
        r.protocolNumber?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [sicknessProtocols, search]);

  const openReview = (item, action) => {
    setReviewTarget(item);
    setReviewAction(action);
    setReviewNotes('');
  };

  const confirmReview = async () => {
    if (!reviewTarget || !reviewAction) return;
    try {
      const updated = await apiReviewVacation(reviewTarget.id, {
        status: reviewAction === 'approve' ? 'approved' : 'rejected',
        reviewNotes: reviewNotes.trim()
      });
      setVacationRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      toast.success(reviewAction === 'approve' ? t('hr.requests.approved') : t('hr.requests.rejected'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    }
    setReviewTarget(null);
    setReviewAction(null);
    setReviewNotes('');
  };

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="p-4 bg-white rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-900 leading-tight">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="h-14 border-b border-slate-200 bg-white px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <HCLogo size="md" />
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{t('hr.dashboard.title')}</div>
            <div className="text-[11px] text-slate-500 leading-tight">{t('hr.dashboard.subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector size="sm" />
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-semibold">
              {user?.initials}
            </div>
            <div className="text-xs">
              <div className="font-medium text-slate-900">{user?.name}</div>
              <div className="text-slate-500">{t('login.role.hr')}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5">
            <LogOut className="w-4 h-4" /> {t('common.logout')}
          </Button>
        </div>
      </header>

      <AppSwitcher />

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">{t('hr.dashboard.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('hr.dashboard.subtitle')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <Stat icon={Fingerprint} label={t('hr.dashboard.todayAttendance')} value={stats.presentToday} color="bg-emerald-100 text-emerald-700" />
          <Stat icon={Hourglass} label={t('hr.dashboard.pendingRequests')} value={stats.pending} color="bg-amber-100 text-amber-700" />
          <Stat icon={Stethoscope} label={t('hr.requests.sickness')} value={stats.sickness} color="bg-rose-100 text-rose-700" />
          <Stat icon={Building2} label={t('hr.dashboard.workplaces')} value={stats.workplaces} color="bg-blue-100 text-blue-700" />
          <Stat icon={Users} label={t('hr.dashboard.employees')} value={stats.employees} color="bg-indigo-100 text-indigo-700" />
          <Stat icon={CheckCircle2} label={t('hr.requests.approved')} value={stats.approved} color="bg-teal-100 text-teal-700" />
        </div>

        {/* Main content */}
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
              <TabsTrigger value="attendance" className="gap-2">
                <Fingerprint className="w-4 h-4" /> {t('hr.dashboard.attendance')}
              </TabsTrigger>
              <TabsTrigger value="vacation" className="gap-2">
                <ClipboardList className="w-4 h-4" /> {t('hr.requests.vacation')}
                {stats.pending > 0 && (
                  <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 px-1.5 rounded-full font-semibold">{stats.pending}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="sickness" className="gap-2">
                <Stethoscope className="w-4 h-4" /> {t('hr.requests.sickness')}
              </TabsTrigger>
              <TabsTrigger value="employees" className="gap-2">
                <UserCog className="w-4 h-4" /> {t('hr.dashboard.employees')}
              </TabsTrigger>
              <TabsTrigger value="payslips" className="gap-2">
                <Wallet className="w-4 h-4" /> {t('hr.dashboard.payslips')}
              </TabsTrigger>
              <TabsTrigger value="announcements" className="gap-2">
                <Megaphone className="w-4 h-4" /> {t('hr.dashboard.announcements')}
              </TabsTrigger>
              <TabsTrigger value="workplaces" className="gap-2">
                <Building2 className="w-4 h-4" /> {t('hr.dashboard.workplaces')}
              </TabsTrigger>
              <TabsTrigger value="notifications" className="gap-2" data-testid="tab-notifications">
                <Bell className="w-4 h-4" /> {t('hr.dashboard.notifications')}
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              {tab === 'vacation' && (
                <div className="flex items-center bg-white border border-slate-200 rounded-md p-0.5">
                  {[
                    { v: 'all', label: t('common.all') },
                    { v: 'pending', label: t('hr.requests.pending') },
                    { v: 'approved', label: t('hr.requests.approved') },
                    { v: 'rejected', label: t('hr.requests.rejected') }
                  ].map(o => (
                    <button
                      key={o.v}
                      onClick={() => setFilter(o.v)}
                      className={`text-xs px-2.5 py-1.5 rounded transition-colors ${
                        filter === o.v ? 'bg-blue-700 text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
              {(tab === 'vacation' || tab === 'sickness') && (
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder={t('common.search') + '...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-9 pl-8 w-[200px] text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          <TabsContent value="attendance" className="space-y-4 mt-0">
            <AttendanceDailyView />
            <div className="pt-4 border-t border-slate-200">
              <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-700" /> {t('common.reports')}
              </div>
              <AttendanceMonthlyReport />
            </div>
          </TabsContent>

          <TabsContent value="employees" className="mt-0">
            <EmployeeManager />
          </TabsContent>

          <TabsContent value="payslips" className="mt-0">
            <PayslipManagerHR />
          </TabsContent>

          <TabsContent value="announcements" className="mt-0">
            <AnnouncementManager />
          </TabsContent>

          <TabsContent value="workplaces" className="mt-0">
            <WorkplaceManager />
          </TabsContent>

          <TabsContent value="notifications" className="mt-0">
            <NotificationSettings />
          </TabsContent>

          <TabsContent value="vacation" className="space-y-3 mt-0">
            {filteredVacations.length === 0 ? (
              <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
                <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <div className="text-sm font-medium text-slate-700">{t('hr.requests.noRequests')}</div>
              </div>
            ) : (
              filteredVacations.map(r => (
                <VacationCard
                  key={r.id}
                  item={r}
                  showEmployee
                  actions={r.status === 'pending' ? (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openReview(r, 'reject')} className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800">
                        <XCircle className="w-3.5 h-3.5 mr-1" /> {t('hr.requests.reject')}
                      </Button>
                      <Button size="sm" onClick={() => openReview(r, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t('hr.requests.approve')}
                      </Button>
                    </div>
                  ) : null}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="sickness" className="space-y-3 mt-0">
            {filteredSickness.length === 0 ? (
              <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
                <Stethoscope className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <div className="text-sm font-medium text-slate-700">{t('hr.requests.noRequests')}</div>
              </div>
            ) : (
              filteredSickness.map(r => <SicknessCard key={r.id} item={r} showEmployee />)
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Review dialog */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewAction === 'approve' ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> {t('hr.requests.approve')}</>
              ) : (
                <><AlertCircle className="w-5 h-5 text-red-600" /> {t('hr.requests.reject')}</>
              )}
            </DialogTitle>
            <DialogDescription>
              {reviewTarget?.employeeName} — {reviewTarget?.days} {t('hr.requests.days')} ({reviewTarget && formatDate(reviewTarget.startDate)} → {reviewTarget && formatDate(reviewTarget.endDate)})
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-700 block mb-2">
              {t('common.notes')} {reviewAction === 'reject' && <span className="text-red-500">*</span>}
            </label>
            <Textarea
              placeholder={t('common.notes')}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => {
                if (reviewAction === 'reject' && !reviewNotes.trim()) {
                  toast.error(t('common.required'));
                  return;
                }
                confirmReview();
              }}
              className={reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {reviewAction === 'approve' ? t('hr.requests.approve') : t('hr.requests.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRDashboardPage;
